import { FormControl, FormLabel, Button, Box, Input, HStack, PopoverContent, PopoverTrigger, Popover, Portal, Tag, TagLabel, TagCloseButton, Text, Badge } from '@chakra-ui/react'
import { FaChevronDown } from 'react-icons/fa'
import { theme } from 'styles'
import { useSelector } from 'react-redux'
import { set, useForm } from 'react-hook-form'
import Select from 'components/Select'
import { useEffect, useState } from 'react'
import { USER_TYPES } from 'constants'
import DateInput from 'components/DateInput'
import MultiDatePicker, {Calendar } from "react-multi-date-picker";
import DatePanel from "react-multi-date-picker/plugins/date_panel" 
import useAuthorization from 'hooks/use-authorization'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setToken } from 'store/modules/user'

// Human labels for the chips. Keys match the react-hook-form field names, which
// are not always obvious from the UI ("avaibility" is the Available Dates
// picker, and the typo is load-bearing — it is what the server reads).
const FILTER_LABELS = {
  roster: 'Search option',
  first_name: 'First name',
  last_name: 'Last name',
  gender: 'Gender',
  min_age: 'Min age',
  max_age: 'Max age',
  avaibility: 'Available dates',
  available_for: 'Available for',
  other_abled: 'Other abled',
  cloth_size: 'Clothing size',
  min_height: 'Min height',
  max_height: 'Max height',
  union: 'Union status',
  ethnicity_look: 'Racial appearance',
  shoe_size: 'Shoe size',
  ethnicity_actual: 'Ethnic heritage',
  hair_color: 'Hair color',
  region: 'Region',
  min_weight: 'Min weight',
  max_weight: 'Max weight',
  eye_color: 'Eye color',
  is_twin: 'Identical twin',
  special_skills: 'Special skills',
  last_updated_months: 'Updated within',
  not_updated_last_months: 'Not updated within',
  influencer: 'Influencer',
  body_type: 'Body type',
  is_homeschooled: 'Homeschooled',
  submitted_date: 'Submitted after',
  additional_searches: 'Additional search'
}

// Fields the form seeds on reset. A value equal to its default is not a filter
// the user chose, so it should not show as an active chip — roster defaults to
// 'roster' and was appearing on every search before anyone touched anything.
const FILTER_DEFAULTS = {
  roster: 'roster'
}

const isFilterSet = (field, value) => {
  if (value === null || value === undefined || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (FILTER_DEFAULTS[field] !== undefined && value === FILTER_DEFAULTS[field]) return false
  return true
}

// Arrays clear back to [], everything else to ''. Matching the shape the form
// started with matters: handing a Select `undefined` makes it uncontrolled.
const emptyValueFor = (value) => (Array.isArray(value) ? [] : '')

const FormControlItem = (props) => {
  return (
    <FormControl {...props} minW={52} flex={1}>
      {props.children}
    </FormControl>
  )
}

// The 24 filters below the fold, grouped by what someone is actually thinking
// about when they reach for them. Nothing is removed — every control that was
// in the flat list is still here, just filed. The six a caster touches most
// (search option, name, age, gender) stay above the fold, always visible.
const SECTIONS = [
  { key: 'physical', title: 'Physical',
    fields: ['min_height', 'max_height', 'min_weight', 'max_weight', 'body_type',
      'hair_color', 'eye_color', 'cloth_size', 'shoe_size', 'is_twin'] },
  { key: 'background', title: 'Background & Identity',
    fields: ['ethnicity_actual', 'ethnicity_look', 'union', 'is_homeschooled', 'other_abled'] },
  { key: 'work', title: 'Work & Availability',
    fields: ['available_for', 'avaibility', 'special_skills', 'influencer', 'additional_searches'] },
  { key: 'location', title: 'Location & Recency',
    fields: ['region', 'submitted_date', 'last_updated_months', 'not_updated_last_months'] }
]

// No overflow='hidden' on the section box. It was there so the tinted header
// would not bleed past the rounded corners, but it also clipped every Select
// menu on a section's last row: the menu opened, then got cut off at the
// section edge, which read as the dropdown doing nothing. The header carries
// its own radius instead, so the corners stay clean and menus can overflow.
const FilterSection = ({ title, count, isOpen, onToggle, children }) => (
  <Box w='100%' bg='white' borderWidth='1px' borderColor={theme.line}
    borderRadius='10px' mb={3}>
    <Box
      as='button'
      type='button'
      onClick={onToggle}
      aria-expanded={isOpen}
      w='100%'
      display='flex'
      alignItems='center'
      gap={2}
      px={4}
      py={3}
      borderTopRadius='9px'
      borderBottomRadius={isOpen ? '0' : '9px'}
      bg={isOpen ? theme.greenTint : 'white'}
      _hover={{ bg: theme.greenTint }}
    >
      <Text fontSize='13px' fontWeight='700' letterSpacing='0.04em'
        textTransform='uppercase' color={theme.ink}>{title}</Text>
      {/* The count is what keeps a collapsed section honest: a filter folded
          away in here still announces itself on the header. */}
      {count > 0 && (
        <Badge borderRadius='999px' px={2} bg={theme.green} color='white'
          fontSize='11px' fontWeight='700'>{count}</Badge>
      )}
      <Box ml='auto' color={theme.muted} display='flex' alignItems='center'
        transform={isOpen ? 'rotate(180deg)' : 'none'} transition='transform .15s'>
        <FaChevronDown size={12} />
      </Box>
    </Box>
    {/* Hidden rather than unmounted. react-hook-form would hold the values
        either way at v7, but this also avoids remounting ten Selects per toggle. */}
    <Box display={isOpen ? 'block' : 'none'} px={4} pt={3} pb={4}>
      <Box display='flex' flexWrap='wrap' gridGap={3}>{children}</Box>
    </Box>
  </Box>
)

const ClientSearch = ({
  search = {},
  searchSubmit = () => {}
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } =  useAuthorization();
  const enumData = useSelector(state => state.talent.enumData)
  const user = useSelector(state => state.user.currentUser)

  const {
    control,
    handleSubmit,
    register,
    setValue,
    reset,
    watch
  } = useForm({
    roster: 'roster',
    avaibility: []
  })
  useEffect(() => {
    if (search) {
      reset({
        avaibility: [],
        roster: 'roster',
        ...search
      })
    }
  }, [JSON.stringify(search)])

  const avaibility= watch('avaibility')
  const watchedFields = watch();

  const activeFilters = Object.keys(FILTER_LABELS)
    .filter((field) => isFilterSet(field, watchedFields[field]))
    .map((field) => ({ field, label: FILTER_LABELS[field] }))

  useEffect(() => {
    if (!isAuthenticated()) {
      dispatch(setToken(null))
      navigate('/auth/login')
    }
  }, [watchedFields]);

  // Only ever opens a section, never forces one shut: loading a saved search
  // that sets a hair color should reveal that control, but a section the user
  // opened by hand stays open when they clear the last chip inside it.
  const [openSections, setOpenSections] = useState({})
  useEffect(() => {
    const opened = {}
    SECTIONS.forEach((section) => {
      if (section.fields.some((f) => isFilterSet(f, (search || {})[f]))) {
        opened[section.key] = true
      }
    })
    if (Object.keys(opened).length) {
      setOpenSections((prev) => ({ ...prev, ...opened }))
    }
  }, [JSON.stringify(search)])

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))

  const countActive = (fields) =>
    fields.filter((f) => isFilterSet(f, watchedFields[f])).length

  return (
    <form onSubmit={handleSubmit(searchSubmit)} w="100%">
      {/* There was no way to see what was actually filtering without scrolling
          all 29 controls. Chips surface the active ones and let each be cleared
          individually. Reads watch() and calls setValue — no control moves. */}
      {activeFilters.length > 0 && (
        <Box
          display='flex'
          flexWrap='wrap'
          alignItems='center'
          gap={2}
          mb={4}
          px={3}
          py={3}
          bg={theme.greenTint}
          borderWidth='1px'
          borderColor={theme.line}
          borderRadius='10px'
        >
          <Text fontSize='11px' fontWeight='700' letterSpacing='0.1em'
            textTransform='uppercase' color={theme.muted} mr={1}>
            {activeFilters.length} active
          </Text>
          {activeFilters.map(({ field, label }) => (
            <Tag key={field} size='sm' borderRadius='999px' bg='white'
              borderWidth='1px' borderColor={theme.line} color={theme.ink}>
              <TagLabel fontSize='12px'>{label}</TagLabel>
              <TagCloseButton
                aria-label={`Clear ${label}`}
                onClick={() => setValue(
                  field,
                  FILTER_DEFAULTS[field] !== undefined
                    ? FILTER_DEFAULTS[field]
                    : emptyValueFor(watchedFields[field])
                )}
              />
            </Tag>
          ))}
          <Button
            size='xs'
            variant='ghost'
            ml='auto'
            fontSize='12px'
            color={theme.muted}
            onClick={() => reset()}
          >
            Clear all
          </Button>
        </Box>
      )}

      {/* Above the fold: who you are looking for. */}
      <Box w='100%' bg='white' borderWidth='1px' borderColor={theme.line}
        borderRadius='10px' px={4} py={4} mb={3}>
        <Box display='flex' flexWrap='wrap' gridGap={3}>
          {user && user.user_type === USER_TYPES.ADMIN && (
            <FormControl minW='100%'>
              <FormLabel>Search Option</FormLabel>
              <Select control={control}
                name='roster'
                options={[{
                  label: 'Roster',
                  value: 'roster'
                }, {
                  label: 'Non-Roster',
                  value: 'non_roster'
                }, {
                  label: 'All',
                  value: 'all'
                }]}
                onChange={({ value }) => {
                  setValue('roster', value)
                }}
              />
            </FormControl>
          )}
          <FormControlItem>
            <FormLabel>First Name</FormLabel>
            <Input {...register('first_name')}/>
          </FormControlItem>
          <FormControlItem>
            <FormLabel>Last Name</FormLabel>
            <Input {...register('last_name')}/>
          </FormControlItem>
          <FormControlItem>
            <FormLabel>Gender</FormLabel>
            <Select control={control}
              isMulti
              name='gender'
              options={enumData.gender}
            />
          </FormControlItem>
          <HStack flex={1} flexDirection={{base: 'column', sm: 'row'}}>
            <FormControlItem>
              <FormLabel>Min Age</FormLabel>
              <Select control={control}
                name='min_age'
                options={enumData.age}
              />
            </FormControlItem>
            <FormControlItem>
              <FormLabel>Max Age</FormLabel>
              <Select control={control}
                name='max_age'
                options={enumData.age}
              />
            </FormControlItem>
          </HStack>
        </Box>
      </Box>

      <FilterSection
        title='Physical'
        count={countActive(SECTIONS[0].fields)}
        isOpen={!!openSections.physical}
        onToggle={() => toggleSection('physical')}
      >
        <HStack flex={1} flexDirection={{base: 'column', sm: 'row'}}>
          <FormControlItem>
            <FormLabel>Min Height</FormLabel>
            <Select control={control}
              name='min_height'
              options={enumData.height}
            />
          </FormControlItem>
          <FormControlItem>
            <FormLabel>Max Height</FormLabel>
            <Select control={control}
              name='max_height'
              options={enumData.height}
            />
          </FormControlItem>
        </HStack>
        <HStack flex={1} flexDirection={{base: 'column', sm: 'row'}}>
          <FormControlItem>
            <FormLabel>Min Weight</FormLabel>
            <Select control={control}
              name='min_weight'
              options={enumData.weight}
            />
          </FormControlItem>
          <FormControlItem>
            <FormLabel>Max Weight</FormLabel>
            <Select control={control}
              name='max_weight'
              options={enumData.weight}
            />
          </FormControlItem>
        </HStack>
        <FormControlItem>
          <FormLabel>Body Type</FormLabel>
          <Select control={control}
            isMulti
            name='body_type'
            options={enumData.body_type}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Hair Color</FormLabel>
          <Select control={control}
            isMulti
            name='hair_color'
            options={enumData.hair_color}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Eye Color</FormLabel>
          <Select control={control}
            isMulti
            name='eye_color'
            options={enumData.eye_color}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Clothing Size</FormLabel>
          <Select control={control}
            isMulti
            name='cloth_size'
            options={enumData.cloth_size}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Shoe Size</FormLabel>
          <Select control={control}
            isMulti
            name='shoe_size'
            options={enumData.shoe_size}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Is identical twin</FormLabel>
          <Select control={control}
            name='is_twin'
            options={enumData.boolean_value}
          />
        </FormControlItem>
      </FilterSection>

      <FilterSection
        title='Background & Identity'
        count={countActive(SECTIONS[1].fields)}
        isOpen={!!openSections.background}
        onToggle={() => toggleSection('background')}
      >
        <FormControlItem>
          <FormLabel>Ethnic Heritage (actual)</FormLabel>
          <Select control={control}
            isMulti
            name='ethnicity_actual'
            options={enumData.ethnicity}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Racial Appearance</FormLabel>
          <Select control={control}
            isMulti
            name='ethnicity_look'
            options={enumData.racial_appearance}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Union Status</FormLabel>
          <Select control={control}
            isMulti
            name="union"
            options={enumData.union}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Is Homeschooled</FormLabel>
          <Select control={control}
            isMulti
            name='is_homeschooled'
            options={enumData.boolean_value}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Other abled?</FormLabel>
          <Select control={control}
            isMulti
            name='other_abled'
            options={enumData.boolean_value}
          />
        </FormControlItem>
      </FilterSection>

      <FilterSection
        title='Work & Availability'
        count={countActive(SECTIONS[2].fields)}
        isOpen={!!openSections.work}
        onToggle={() => toggleSection('work')}
      >
        <FormControlItem>
          <FormLabel>Job Type</FormLabel>
          <Select control={control}
            isMulti
            name='available_for'
            options={[{
              label: 'Theatrical',
              value: 't'
            }, {
              label: 'Print',
              value: 'p'
            }, {
              label: 'Commercial',
              value: 'c'
            }]}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Special Skills</FormLabel>
          <Select control={control}
            isMulti
            name='special_skills'
            options={enumData.skills}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Influencer</FormLabel>
          <Select control={control}
            name='influencer'
            options={enumData.influencer}
          />
        </FormControlItem>
        {user && user.user_type === USER_TYPES.ADMIN && (
          <FormControlItem>
            <FormLabel>Additional Search</FormLabel>
            <Select control={control}
              isMulti
              name='additional_searches'
              options={enumData.additional_searches}
            />
          </FormControlItem>
        )}
        <FormControlItem>
          <FormLabel>Available dates</FormLabel>
          {/* The calendar goes through a Portal because the section it lives in
              clips overflow (for its rounded corners). Rendered in place, the
              popper had no room below the trigger, so it either got cut off at
              the section edge or flipped up and sat on top of the other
              filters. At body level it can open downward and float over
              whatever is beneath it, as a popover should. */}
          <Popover placement='bottom-start' isLazy>
            <PopoverTrigger>
              <Button type='button'>
                {(!avaibility || avaibility.length === 0) ? 'Not required' : `${avaibility.length} dates selected`}
              </Button>
            </PopoverTrigger>
            <Portal>
              <PopoverContent w='auto' borderWidth='0' bg='transparent' boxShadow='none'>
                <Calendar
                  value={avaibility}
                  multiple={true}
                  onChange={v => {
                    setValue('avaibility', v.map(d => new Date(d.unix * 1000)))
                  }}
                  plugins={[
                    <DatePanel sort="date" />
                  ]}
                />
              </PopoverContent>
            </Portal>
          </Popover>
        </FormControlItem>
      </FilterSection>

      <FilterSection
        title='Location & Recency'
        count={countActive(SECTIONS[3].fields)}
        isOpen={!!openSections.location}
        onToggle={() => toggleSection('location')}
      >
        <FormControlItem>
          <FormLabel>Region</FormLabel>
          <Select control={control}
            isMulti
            name='region'
            options={enumData.region}
          />
        </FormControlItem>
        <FormControlItem>
          <FormLabel>Submitted After</FormLabel>
          <Input
            type='date'
            autoComplete="off"
            {...register('submitted_date')}
            name='submitted_date'
            color={watch('submitted_date') ? 'initial' : 'gray.300'}
            _focus={{ color: 'initial' }}
          />
        </FormControlItem>
        <HStack flex={1} flexDirection={{base: 'column', sm: 'row'}}>
          <FormControlItem>
            <FormLabel textAlign='center'>Profile updated in the last x months</FormLabel>
            <Input type='number' {...register('last_updated_months')}/>
          </FormControlItem>
          <FormControlItem>
            <FormLabel textAlign='center'>Profile not updated in the last x months</FormLabel>
            <Input type='number' {...register('not_updated_last_months')}/>
          </FormControlItem>
        </HStack>
      </FilterSection>

      <Box w='100%' mt={4}>
        <Button mr={2} type='submit'>Search</Button>
        <Button onClick={() => {
          reset()
        }}>Reset Filters</Button>
      </Box>
    </form>
  )
}

export default ClientSearch
